import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

export interface DiscountCode {
  id: string;
  code: string;
  type: 'percent' | 'fixed';
  value: number;
  active: boolean;
  description: string | null;
  valid_until: string | null;
  usage_limit: number | null;
  used_count: number;
  tour_ids: string[] | null;
  created_at: string;
}

export interface DiscountInput {
  code: string;
  type: 'percent' | 'fixed';
  value: number;
  active: boolean;
  description?: string;
  valid_until?: string | null;
  usage_limit?: number | null;
  tour_ids?: string[] | null;
}

export function useDiscountCodes() {
  const [codes, setCodes] = useState<DiscountCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCodes = async (showLoading = false) => {
    if (showLoading) setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('vietnamtravel_discount_codes')
        .select('*')
        .order('created_at', { ascending: false });
      if (err) throw err;
      setCodes((data || []) as DiscountCode[]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load discount codes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCodes(true);
  }, []);

  const createCode = async (input: DiscountInput) => {
    const { error: err } = await supabase.from('vietnamtravel_discount_codes').insert({
      code: input.code.trim().toUpperCase(),
      type: input.type,
      value: input.value,
      active: input.active,
      description: input.description?.trim() || null,
      valid_until: input.valid_until || null,
      usage_limit: input.usage_limit ?? null,
      tour_ids: input.tour_ids && input.tour_ids.length > 0 ? input.tour_ids : null,
    });
    if (err) return { error: err.message };
    await fetchCodes();
    return { error: null };
  };

  const updateCode = async (id: string, input: Partial<DiscountInput>) => {
    const payload: Record<string, unknown> = {};
    if (input.code !== undefined) payload.code = input.code.trim().toUpperCase();
    if (input.type !== undefined) payload.type = input.type;
    if (input.value !== undefined) payload.value = input.value;
    if (input.active !== undefined) payload.active = input.active;
    if (input.description !== undefined) payload.description = input.description?.trim() || null;
    if (input.valid_until !== undefined) payload.valid_until = input.valid_until || null;
    if (input.usage_limit !== undefined) payload.usage_limit = input.usage_limit ?? null;
    if (input.tour_ids !== undefined) {
      payload.tour_ids = input.tour_ids && input.tour_ids.length > 0 ? input.tour_ids : null;
    }

    const { error: err } = await supabase.from('vietnamtravel_discount_codes').update(payload).eq('id', id);
    if (err) return { error: err.message };
    await fetchCodes();
    return { error: null };
  };

  const toggleActive = async (id: string, active: boolean) => {
    const { error: err } = await supabase.from('vietnamtravel_discount_codes').update({ active }).eq('id', id);
    if (err) return { error: err.message };
    await fetchCodes();
    return { error: null };
  };

  const deleteCode = async (id: string) => {
    const { error: err } = await supabase.from('vietnamtravel_discount_codes').delete().eq('id', id);
    if (err) return { error: err.message };
    await fetchCodes();
    return { error: null };
  };

  return {
    codes,
    loading,
    error,
    refresh: fetchCodes,
    createCode,
    updateCode,
    toggleActive,
    deleteCode,
  };
}